import { motion } from "framer-motion";
import {
  ArrowRight,
  Boxes,
  LineChart,
  Percent,
  Radar,
  ShieldAlert,
  Zap,
  BellRing,
  TrendingUp,
} from "lucide-react";

const PILLARS = [
  {
    icon: Zap,
    title: "Agility",
    body: "Avoid inventory shortfalls and buildups with real-time stock visibility and early signals.",
  },
  {
    icon: BellRing,
    title: "No Surprises",
    body: "Stay ahead of risks with live signals so that you are never caught off guard.",
  },
  {
    icon: TrendingUp,
    title: "Consistent Growth",
    body: "Build confidence into every decision. When the whole organization sees the same truth, execution becomes coherent and scalable.",
  },
];

const FEATURES = [
  { icon: Boxes, title: "Inventory", body: "Real-time stock monitoring to avoid over- or under-stocking." },
  { icon: LineChart, title: "Sales", body: "Performance by market, channel, customer and product line." },
  { icon: Percent, title: "Pricing", body: "Dynamic rates, margin health, and competitive pressure." },
  { icon: Radar, title: "Market Dynamics", body: "Demand signals and competitive moves in one place." },
  { icon: ShieldAlert, title: "Compliance Risk", body: "Compliance risk and policy changes before they hit." },
];

const LIGHTS = [
  {
    label: "Green",
    accent: "text-green-700",
    bar: "bg-green-500",
    tint: "bg-green-50",
    ring: "ring-green-200",
    heading: "On track",
    body: "Everything running as planned. No action needed — maintain course and protect the upside.",
  },
  {
    label: "Amber",
    accent: "text-amber-700",
    bar: "bg-amber-500",
    tint: "bg-amber-50",
    ring: "ring-amber-200",
    heading: "Watch closely",
    body: "Early warning signals detected. Investigate and act before small issues become costly problems.",
  },
  {
    label: "Red",
    accent: "text-red-700",
    bar: "bg-red-500",
    tint: "bg-red-50",
    ring: "ring-red-200",
    heading: "Act now",
    body: "Critical thresholds breached. Prioritize response now to protect revenue, stock or compliance.",
  },
];

export default function OperationalDashboard() {
  return (
    <section
      id="operational-dashboard"
      data-testid="operational-dashboard-section"
      className="relative bg-slate-50 py-24 lg:py-32 overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <span
              data-testid="dashboard-new-badge"
              className="inline-flex items-center gap-2 bg-teal-700 text-white text-[11px] font-semibold uppercase tracking-[0.2em] px-3 py-1.5 animate-pulse"
            >
              <span className="w-1.5 h-1.5 bg-white rounded-full" />
              New
            </span>
          </motion.div>

          <motion.h2
            data-testid="dashboard-heading"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1] animate-pulse-slow"
          >
            Unlock Clearer Business Decisions
            <span className="block text-teal-700 mt-2 text-2xl sm:text-3xl lg:text-4xl font-normal">
              Operational Dashboard
            </span>
          </motion.h2>

          <p className="mt-6 text-slate-600 text-lg leading-relaxed">
            From data noise to confident action —{" "}
            <span className="text-slate-900 font-medium">
              reports tell you what happened; this dashboard tells you what to do next.
            </span>
          </p>
          <p className="mt-3 text-xs uppercase tracking-[0.2em] text-teal-700 font-medium">
            Customized live operational intelligence
          </p>
        </div>

        {/* Why It Matters — 3 pillars */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                data-testid={`dashboard-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-slate-200 bg-white p-6"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 bg-teal-50 text-teal-700 border border-teal-100">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Your Data, One Dashboard */}
        <div className="mt-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-teal-700 font-medium">Your Data, One Dashboard</div>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight">
                Five signals. One view. Zero guesswork.
              </h3>
            </div>
            <p className="text-slate-600 text-sm max-w-md">
              Every dimension that moves your business — sourced, correlated and displayed live in one operational canvas.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  data-testid={`dashboard-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="group relative border border-slate-200 bg-white p-6 hover:border-teal-500 transition-colors"
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 bg-teal-50 text-teal-700 border border-teal-100 group-hover:bg-teal-700 group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <h4 className="mt-5 font-display text-base font-semibold text-slate-900">{f.title}</h4>
                  <p className="mt-2 text-xs text-slate-600 leading-relaxed">{f.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Traffic-light system */}
        <div className="mt-20">
          <div className="max-w-3xl mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-teal-700 font-medium">Instant Operational Insights</div>
            <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight">
              Traffic-light clarity for every decision.
            </h3>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Know exactly where to focus. <span className="text-green-700 font-medium">Green</span> means smooth.{" "}
              <span className="text-amber-700 font-medium">Amber</span> means watch.{" "}
              <span className="text-red-700 font-medium">Red</span> means act now.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {LIGHTS.map((l, i) => (
              <motion.div
                key={l.label}
                data-testid={`dashboard-light-${l.label.toLowerCase()}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`relative border border-slate-200 bg-white p-6 ring-1 ${l.ring}`}
              >
                <div className={`absolute top-0 left-0 right-0 h-1 ${l.bar}`} />
                <div className="flex items-center gap-3">
                  <span className={`relative inline-flex w-3.5 h-3.5 ${l.bar} rounded-full`}>
                    <span
                      className={`absolute inset-0 rounded-full ${l.bar} opacity-60 animate-ping`}
                      style={{ animationDuration: "2s" }}
                    />
                  </span>
                  <span className={`text-xs uppercase tracking-[0.2em] font-semibold ${l.accent}`}>
                    {l.label}
                  </span>
                </div>
                <h4 className="mt-5 font-display text-lg font-semibold text-slate-900">{l.heading}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{l.body}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-slate-200 pt-12">
          <div>
            <h4 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight max-w-xl">
              Ready to see your operations in one clear view?
            </h4>
            <p className="mt-3 text-slate-600 text-sm">
              Talk to our team about a tailored dashboard for your business.
            </p>
          </div>
          <a
            href="#contact"
            data-testid="dashboard-talk-to-us"
            className="group inline-flex items-center bg-teal-700 hover:bg-teal-800 text-white px-8 h-14 text-sm font-semibold tracking-wide transition-colors whitespace-nowrap"
          >
            Talk to Us
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2.25} />
          </a>
        </div>
      </div>
    </section>
  );
}
